import { FC, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useGlobalMessageActions } from "../../../entities/globalMessage";
import { useMyActions } from "../../../entities/my";
import { ITrialLesson, trialLessonService } from "../../../entities/trialLesson";
import { ISlotPattern } from "../../../entities/schedule";
import { IStudentTag, studentService } from "../../../entities/student";
import { AuthError } from "../../../shared/lib/error/AuthError";
import { MyError } from "../../../shared/lib/error/MyError";
import { MyButton, MyButtonSecondary } from "../../../shared/ui/button";
import { MyInput } from "../../../shared/ui/input";
import { DropDownListSelected } from "../../../shared/ui/dropDownSelected";
import { Modal } from "../../../shared/ui/modal";
import { ScheduleWeekGrid } from "../../../widgets/scheduleCalendar";
import { formatMoscowDateInput } from "../../../shared/lib/helpers/moscowDateTime";
import {
    getTrialStudentForm,
    ITrialStudentForm,
    validateTrialStudentForm,
} from "../lib/trialStudentForm";
import classes from './trialLessonCreateStudent.module.scss'

interface IProps {
    open: boolean;
    setOpen: (open: boolean) => void;
    trialLesson: ITrialLesson;
    onSuccess: () => void;
}

const getDefaultStartsOn = (): string => {
    const tomorrow = new Date()
    tomorrow.setDate(tomorrow.getDate() + 1)
    return formatMoscowDateInput(tomorrow)
}

export const TrialLessonCreateStudent: FC<IProps> = ({open, setOpen, trialLesson, onSuccess}) => {

    const {setIsAuth} = useMyActions()
    const {setGlobalMessage} = useGlobalMessageActions()
    const navigate = useNavigate()

    const [form, setForm] = useState<ITrialStudentForm>(() => getTrialStudentForm(trialLesson))
    const [error, setError] = useState<string>('')
    const [isSaving, setIsSaving] = useState<boolean>(false)
    const [tags, setTags] = useState<IStudentTag[]>([])
    const [selectedTagIds, setSelectedTagIds] = useState<number[]>([])
    const [startsOn, setStartsOn] = useState<string>(getDefaultStartsOn)
    const [weeklySlots, setWeeklySlots] = useState<ISlotPattern[]>([])

    useEffect(() => {
        if(!open) return

        setForm(getTrialStudentForm(trialLesson))
        setSelectedTagIds([])
        setStartsOn(getDefaultStartsOn())
        setWeeklySlots([])
        setError('')

        const loadTags = async () => {
            try{
                setTags(await studentService.getTags())
            }
            catch(e){
                console.log(e)
                setGlobalMessage({message: 'Ошибка при получении тегов студентов', type: 'error'})
            }
        }
        loadTags()
    }, [open, setGlobalMessage, trialLesson])

    const onClose = (value: boolean) => {
        if(!value) setError('')
        setOpen(value)
    }

    const setFormValue = (key: keyof ITrialStudentForm) => (value: string) => {
        setForm(current => ({...current, [key]: value}))
        setError('')
    }

    const setSelectedTags = (ids: number[]) => {
        setSelectedTagIds(ids)
        setError('')
    }

    const dateInputToLocalDate = (value: string) => {
        const [year, month, day] = value.split('-').map(Number)
        if(!year || !month || !day) return null
        return {year, month, day}
    }

    const buildRegularSchedule = () => {
        if(!weeklySlots.length) return undefined

        const startDate = dateInputToLocalDate(startsOn)
        if(!startDate){
            setError('Укажите дату старта регулярного графика')
            return null
        }

        return {
            startsOn: startDate,
            weeklySlots,
        }
    }

    const createStudent = async () => {
        const result = validateTrialStudentForm(form)

        if(!result.data){
            setError(result.error ?? 'Проверьте данные студента')
            return
        }

        try{
            setIsSaving(true)
            const regularSchedule = buildRegularSchedule()
            if(regularSchedule === null) return

            const response = await trialLessonService.convertTrialLessonToStudent({
                trialLessonId: trialLesson.id,
                student: {
                    ...result.data,
                    tagIds: selectedTagIds.map(String),
                    regularSchedule,
                },
            })

            setGlobalMessage({message: 'Студент создан', type: 'ok'})
            onClose(false)

            if(response.createStudentUrl){
                navigate(response.createStudentUrl)
                return
            }

            onSuccess()
        }
        catch(e){
            console.log(e)

            if(e instanceof AuthError){
                setIsAuth(false)
                setGlobalMessage({message: e.message, type: 'error'})
            }
            else if(e instanceof MyError){
                setGlobalMessage({message: e.message, type: 'error'})
            }
            else{
                setGlobalMessage({message: 'Ошибка при создании студента', type: 'error'})
            }
        }
        finally{
            setIsSaving(false)
        }
    }

    return (
        <Modal open={open} setOpen={onClose}>
            <section className={classes.modal}>
                <section className={classes.header}>
                    <section>
                        <h3>Создать студента</h3>
                        <p>Исполнитель, тег и рабочий Telegram возьмутся из пробного занятия.</p>
                    </section>
                    <button type="button" className={classes.closeButton} onClick={() => onClose(false)}>×</button>
                </section>

                <section className={classes.content}>
                    <section className={classes.section}>
                        <h4>Студент</h4>
                        <section className={classes.formGrid}>
                            <MyInput title="Фамилия" value={form.lastName} setValue={setFormValue('lastName')} setError={setError} isLoading={isSaving} />
                            <MyInput title="Имя" value={form.firstName} setValue={setFormValue('firstName')} setError={setError} isLoading={isSaving} />
                            <MyInput title="Отчество" value={form.middleName} setValue={setFormValue('middleName')} setError={setError} isLoading={isSaving} />
                            <MyInput title="Телефон" value={form.phone} setValue={setFormValue('phone')} setError={setError} isLoading={isSaving} />
                            <MyInput title="Telegram" value={form.telegram} setValue={setFormValue('telegram')} setError={setError} isLoading={isSaving} />
                            <MyInput title="Стоимость часа" value={form.costPerHour} setValue={setFormValue('costPerHour')} setError={setError} isLoading={isSaving} inputMode="decimal" />
                        </section>
                    </section>

                    <section className={classes.section}>
                        <h4>Родитель</h4>
                        <section className={classes.formGrid}>
                            <MyInput title="ФИО родителя" value={form.parentFullName} setValue={setFormValue('parentFullName')} setError={setError} isLoading={isSaving} />
                            <MyInput title="Телефон родителя" value={form.parentPhone} setValue={setFormValue('parentPhone')} setError={setError} isLoading={isSaving} />
                            <MyInput title="Telegram родителя" value={form.parentTelegram} setValue={setFormValue('parentTelegram')} setError={setError} isLoading={isSaving} />
                        </section>
                    </section>

                    <section className={classes.section}>
                        <h4>Теги студента</h4>
                        <DropDownListSelected
                            isLoading={false}
                            items={tags.map(tag => ({id: tag.id, name: tag.tag_name}))}
                            selectedIdItems={selectedTagIds}
                            onSelected={item => selected => {
                                const ids = selected
                                    ? [...selectedTagIds, item.id]
                                    : selectedTagIds.filter(id => id !== item.id)
                                setSelectedTags(ids)
                            }}
                            onSelectAll={setSelectedTags}
                            selectedCount
                            search
                            searchPlaceholder="Поиск тега"
                            placeholder="Выберите теги"
                            emptyText="Теги не созданы"
                        />
                    </section>

                    <section className={classes.section}>
                        <h4>Регулярный график</h4>
                        <section className={classes.scheduleHeader}>
                            <MyInput type="date" title="Начать с" value={startsOn} setValue={value => {
                                setStartsOn(value)
                                setError('')
                            }} setError={setError} isLoading={isSaving} />
                            <span className={classes.scheduleHint}>Если слоты не выбрать, студент создастся без постоянного графика.</span>
                        </section>
                        <section className={classes.scheduleGrid}>
                            <ScheduleWeekGrid
                                value={weeklySlots}
                                onChange={slots => {
                                    setWeeklySlots(slots)
                                    setError('')
                                }}
                                editable={!isSaving}
                                allowUnavailableSelection
                                variant="regular"
                            />
                        </section>
                    </section>
                </section>

                <section className={classes.actions}>
                    <section className={classes.cancelButton}>
                        <MyButtonSecondary onClick={() => onClose(false)} disabled={isSaving}>Отмена</MyButtonSecondary>
                    </section>
                    <section className={classes.submitButton}>
                        <MyButton isLoading={isSaving} error={error} onClick={createStudent}>Создать</MyButton>
                    </section>
                </section>
            </section>
        </Modal>
    )
}
